import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./checkoutPage.css";
import { CombinedContext } from "../../context/combinedContext";
import billingRequest from "../../request/billing";
import translations from "../../translations/translations";
import CheckoutSkeleton from "./checkoutPageSkeleton/checkoutPageSkeleton";

const CheckoutPage = () => {
	const { userData, language } = useContext(CombinedContext);

	const [showStillThere, setShowStillThere] = useState(false);

	const intervalRef = useRef(null);
	const startTimeRef = useRef(null);

	const user = userData?.user;
	const shop = userData?.shop;

	const location = useLocation();
	const navigate = useNavigate();
	const openedRef = useRef(false);

	const billingData = location.state?.billingData;
	const [loading, setLoading] = useState(!billingData);
	const [localBillingData, setLocalBillingData] = useState(billingData);

	const startPolling = () => {
		startTimeRef.current = Date.now();
		setShowStillThere(false);

		intervalRef.current = setInterval(async () => {
			const elapsed = Date.now() - startTimeRef.current;

			// ⏱ Stop after 15 minutes
			if (elapsed >= 15 * 60 * 1000) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
				setShowStillThere(true);
				return;
			}

			try {
				const status = await billingRequest.billingStatus(shop.shopId, userData.cognitoTokens.AccessToken);

				if (status.active) {
					clearInterval(intervalRef.current);
					window.location.href = "/calendar?showLiveBanner=true";
				}
			} catch (err) {
				console.error("Polling error:", err);
			}
		}, 4000); // 🔥 Fixed 4 seconds
	};

	useEffect(() => {
		if (!user || !shop) return;

		if (user.role !== "owner" || !shop.setup) {
			navigate("/calendar");
		}
	}, [user, shop, navigate]);

	useEffect(() => {
		const recover = async () => {
			if (!billingData && shop && user) {
				try {
					setLoading(true);

					const res = await billingRequest.billingSetup({
						shopId: shop.shopId,
						userId: user.userId,
						accessToken: userData.cognitoTokens.AccessToken,
					});

					setLocalBillingData(res); // 👈 no navigation
				} catch (err) {
					console.error("Failed to recover billing setup", err);
					navigate("/calendar");
				} finally {
					setLoading(false);
				}
			}
		};

		recover();
	}, [billingData, shop, user, navigate]);

	useEffect(() => {
		if (!localBillingData) return;
		if (!window.Paddle?.Checkout?.open) return;
		if (openedRef.current) return;

		openedRef.current = true;

		let interval; // so we can clean it up late

		const paddleLocaleMap = {
			en: "en",
			es: "es",
			fr: "fr",
		};

		const paddleLocale = paddleLocaleMap[language] || "en";

		window.Paddle.Checkout.open({
			settings: {
				displayMode: "inline",
				frameTarget: "paddle-checkout-container",
				frameInitialHeight: 650,
				locale: paddleLocale,
			},
			transactionId: localBillingData.transactionId,
		});

		// ✅ Start polling immediately
		startPolling();

		// ✅ Cleanup if component unmounts
		return () => {
			if (interval) clearInterval(interval);
		};
	}, [localBillingData, shop, language]);

	const handleContinue = () => {
		startPolling();
	};

	if (loading || !localBillingData) {
        return (
            <div className="sf-checkout-page">
                <div className="sf-checkout-card">
                    <CheckoutSkeleton />
                </div>
            </div>
        );
    }


	return (
		<div className="sf-checkout-page">
			<div className="sf-checkout-card">
				<h1>{translations[language].checkoutPage.activateTitle}</h1>

				<div className="sf-checkout-paddle-wrapper">
					<div className="paddle-checkout-container"></div>
					{showStillThere && (
						<div className="sf-checkout-still-there">
							<p>{translations[language].checkoutPage.stillThere}</p>
							<button className="sf-checkout-continue-btn" onClick={handleContinue}>
								{translations[language].checkoutPage.continue}
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CheckoutPage;
